// src/pages/pharmacies/PharmacyScanPrescription.jsx
import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { BrowserQRCodeReader } from "@zxing/browser";
import Button from "@/components/ui/Button.jsx";
import PharmacyDesktopHeader from "@/components/nav/PharmacyDesktopHeader.jsx";
import ContextFeedbackLauncher from "@/components/feedback/ContextFeedbackLauncher.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import TopBar from "@/sot/primitives/TopBar.jsx";
import Sheet from "@/sot/primitives/Sheet";
import {
  buildPrescriptionVerifyPath,
  buildPrescriptionVerifyState,
  extractPrescriptionUuid,
  savePrescriptionVerifyContext,
} from "@/utils/prescriptionVerification.js";

const SCAN_ROUTE = "/pharmacy/prescriptions/scan";
const SURFACE_CLASS =
  "rounded-[12px] bg-[rgb(var(--clr-surface-cont-highest))] shadow-[0px_0px_0px_1px_rgba(24,50,119,0.08),0px_1.5px_0px_-0.5px_rgba(26,27,35,0.05),0px_3px_3px_-1.5px_rgba(26,27,35,0.05),0px_6px_12px_-6px_rgba(29,79,215,0.05)]";
const MUTED_TEXT = "text-[rgb(var(--clr-on-surface-variant))]";
const PRIMARY_TEXT = "text-[rgb(var(--clr-on-surface))]";

function extractVerifyTarget(rawValue = "") {
  const rawText = String(rawValue || "").trim();
  const prescriptionId = extractPrescriptionUuid(rawText);
  if (!prescriptionId) return { prescriptionId: "", targetPath: "" };

  let targetPath = buildPrescriptionVerifyPath(prescriptionId);

  try {
    const parsed = new URL(rawText);
    const isVerifyPath = parsed.pathname.startsWith("/verify/rx/");
    const snapshot = String(parsed.searchParams.get("snapshot") || "").trim();
    if (isVerifyPath && snapshot) {
      targetPath = `/verify/rx/${encodeURIComponent(prescriptionId)}?snapshot=${encodeURIComponent(snapshot)}`;
    }
  } catch {
    // Ignore non-URL values and fall back to the canonical verify path.
  }

  return { prescriptionId, targetPath };
}

export default function PharmacyScanPrescription() {
  const nav = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const manualDraft = String(location.state?.manual || "").trim();

  const videoRef = React.useRef(null);
  const readerRef = React.useRef(null);
  const stopRef = React.useRef(() => {});
  const lockedRef = React.useRef(false);

  const [status, setStatus] = React.useState("starting"); // starting | scanning | error | manual
  const [err, setErr] = React.useState("");
  const [openHelp, setOpenHelp] = React.useState(false);
  const [manual, setManual] = React.useState("");

  React.useEffect(() => {
    if (manualDraft) {
      setManual(manualDraft);
      setOpenHelp(true);
    }
  }, [manualDraft]);

  const canUseCamera = React.useMemo(() => {
    if (typeof window === "undefined") return false;
    const hasGetUserMedia =
      !!navigator?.mediaDevices && typeof navigator.mediaDevices.getUserMedia === "function";
    // iOS Safari typically requires secure context for camera access
    return !!window.isSecureContext && hasGetUserMedia;
  }, []);

  const stop = React.useCallback(() => {
    try {
      stopRef.current?.();
    } catch {}
    stopRef.current = () => {};
    lockedRef.current = false;
  }, []);

  const goVerify = React.useCallback(
    (rawText, entryMethod = "camera") => {
      const { prescriptionId, targetPath } = extractVerifyTarget(rawText);
      if (!prescriptionId || !targetPath) {
        setStatus("error");
        setErr(t("pharmacy.scan.invalidCode", "No valid prescription code found. Paste the full link or UUID."));
        return;
      }

      const verifyState = buildPrescriptionVerifyState({
        from: "pharmacy-scan",
        actorRole: "pharmacy",
        prescriptionId,
        rawText,
        entryMethod,
        returnTo: SCAN_ROUTE,
        returnState: entryMethod === "manual" ? { manual: String(rawText || "").trim() } : null,
      });
      savePrescriptionVerifyContext(verifyState);

      stop();
      nav(targetPath, {
        state: verifyState,
      });
    },
    [nav, stop, t]
  );

  const start = React.useCallback(async () => {
    setErr("");
    lockedRef.current = false;

    if (manualDraft) {
      setStatus("manual");
      setErr(
        t(
          "pharmacy.scan.manualRestored",
          "Manual entry restored. Review or edit the code below before verifying again.",
        ),
      );
      setOpenHelp(true);
      return;
    }

    // ✅ Hard guard: iOS HTTP / insecure context → no camera. Go manual immediately.
    if (!canUseCamera) {
      setStatus("manual");
      setErr(
        t(
          "pharmacy.scan.httpsRequired",
          "Camera scanning needs HTTPS on iPhone. Tap Help to paste the prescription link or UUID.",
        )
      );
      setOpenHelp(true);
      return;
    }

    setStatus("starting");

    const videoEl = videoRef.current;
    if (!videoEl) {
      setStatus("error");
      setErr(t("pharmacy.scan.cameraNotReady", "Camera view is not ready."));
      return;
    }

    try {
      if (!readerRef.current) readerRef.current = new BrowserQRCodeReader();

      setStatus("scanning");

      const controls = await readerRef.current.decodeFromConstraints(
        {
          audio: false,
          video: { facingMode: "environment" },
        },
        videoEl,
        (result) => {
          if (result?.getText && !lockedRef.current) {
            lockedRef.current = true;
            goVerify(result.getText(), "camera");
          }
        }
      );

      stopRef.current = () => {
        try {
          controls?.stop?.();
        } catch {}
      };
    } catch (e) {
      setStatus("manual");
      setErr(
        e?.message ||
          t(
            "pharmacy.scan.cameraDenied",
            "Camera permission denied. Tap Help to paste the prescription link or UUID.",
          )
      );
      setOpenHelp(true);
    }
  }, [canUseCamera, goVerify, manualDraft, t]);

  React.useEffect(() => {
    start();
    return () => stop();
  }, [start, stop]);

  return (
    <div className="min-h-dvh bg-[rgb(var(--clr-bg))] text-[rgb(var(--clr-on-surface))]" data-hide-tabbar="true">
      <PharmacyDesktopHeader section="prescriptions" notifications={[]} />

      <main className="afrimed-rail afrimed-rail--comfortable pt-3 pb-10">
        <TopBar
          title={t("pharmacy.scan.title", "Scan prescription")}
          subtitle={t(
            "pharmacy.scan.subtitle",
            "Verify a walk-in prescription by scanning its QR code or pasting the verification link or UUID.",
          )}
          onBack={() => nav("/pharmacy/prescriptions")}
          titleWrapperClassName="mt-2 mb-5"
        />

        <section className={`${SURFACE_CLASS} overflow-hidden`}>
          <div className="border-b border-[rgba(var(--clr-outline-variant),0.55)] px-5 py-4">
            <div className={`text-title-medium ${PRIMARY_TEXT}`}>
              {t("pharmacy.scan.cameraHeading", "Point the camera at the QR code on the prescription.")}
            </div>
            <div className={`mt-1 text-body-medium ${MUTED_TEXT}`}>
              {t("pharmacy.scan.cameraCopy", "If scanning fails, tap Help to paste the code manually.")}
            </div>
          </div>

          <div className="px-5 py-5 pb-[calc(env(safe-area-inset-bottom)+18px)]">
            <div className="mx-auto max-w-[440px] rounded-[16px] border border-[rgba(var(--clr-outline-variant),0.55)] bg-black overflow-hidden">
              <div className="relative w-full aspect-[3/4]">
                <video
                  ref={videoRef}
                  className="absolute inset-0 h-full w-full object-cover"
                  muted
                  playsInline
                />

                <div className="absolute inset-0 pointer-events-none">
                  <div className="absolute inset-5 rounded-[18px] border border-white/20" />
                  <div className="absolute inset-5 rounded-[18px] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.06)]" />
                </div>

                {!canUseCamera ? (
                  <div className="absolute inset-0 flex items-center justify-center p-6 text-center">
                    <div className="rounded-[12px] bg-black/55 px-4 py-3">
                      <div className="text-body-large text-white">
                        {t("pharmacy.scan.cameraUnavailable", "Camera unavailable")}
                      </div>
                      <div className="mt-1 text-body-small text-white/80">
                        {t(
                          "pharmacy.scan.cameraUnavailableCopy",
                          "Use Help to paste the verification link or prescription UUID.",
                        )}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="sticky bottom-[calc(env(safe-area-inset-bottom)+12px)] z-10 mx-auto mt-4 max-w-[440px] md:static">
              <div className="rounded-[14px] bg-[rgb(var(--clr-surface-cont-highest))]/96 px-0 py-0 backdrop-blur-[10px]">
                <div className={`flex flex-col gap-2 text-body-small ${MUTED_TEXT}`}>
                  <span>
                    {status === "scanning"
                      ? t("pharmacy.scan.scanning", "Scanning…")
                      : t("pharmacy.scan.ready", "Ready to verify a walk-in prescription.")}
                  </span>
                  {err ? <span className="text-[rgb(var(--clr-error))]">{err}</span> : null}
                </div>

                <div className="mt-3 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <Button
                      variant="tonal"
                      onClick={() => {
                        stop();
                        start();
                      }}
                    >
                      {t("common.restart", "Restart")}
                    </Button>
                    <Button variant="tonal" onClick={() => setOpenHelp(true)}>
                      {t("common.help", "Help")}
                    </Button>
                  </div>
                  <ContextFeedbackLauncher
                    section="pharmacy-scan"
                    roleOverride="pharmacy"
                    entity={{ id: "walk-in-scan", label: "walk-in scan", type: "scan" }}
                    context={{
                      status,
                      hasManualDraft: Boolean(manualDraft),
                      canUseCamera,
                    }}
                    triggerLabel={t("feedback.trigger", "Make suggestion")}
                    subtitle={t(
                      "pharmacy.scan.feedbackSubtitle",
                      "Capture this scan step so AfriMed can reopen the exact device and walk-in verification context.",
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Sheet open={openHelp} onClose={() => setOpenHelp(false)}>
        <div className="pt-2 pb-1">
          <div className="px-[var(--rail-pad)]">
            <TopBar
              title={t("pharmacy.scan.helpTitle", "Scan help")}
              onBack={() => setOpenHelp(false)}
              close
            />
          </div>

          <div className="px-[var(--rail-pad)] mt-3">
            <div className={`text-body-small ${MUTED_TEXT}`}>
              {t(
                "pharmacy.scan.helpCopy",
                "Paste the prescription UUID or the full verification URL to continue the same walk-in flow.",
              )}
            </div>

            <div className="mt-3">
              <textarea
                value={manual}
                onChange={(e) => setManual(e.target.value)}
                placeholder={t("pharmacy.scan.helpPlaceholder", "Paste UUID or verification link…")}
                className="w-full min-h-[120px] rounded-[12px] border border-[rgba(var(--clr-outline-variant),0.55)] bg-[rgb(var(--clr-surface))] p-3 text-body-medium text-[rgb(var(--clr-on-surface))] outline-none placeholder:text-[rgb(var(--clr-on-surface-variant))]"
              />
            </div>

            <div className="mt-3 flex items-center justify-end gap-2">
              <Button variant="tonal" onClick={() => setManual("")}>
                {t("common.clear", "Clear")}
              </Button>
              <Button
                onClick={() => {
                  const v = (manual || "").trim();
                  if (!v) return;
                  setOpenHelp(false);
                  goVerify(v, "manual");
                }}
              >
                {t("common.verify", "Verify")}
              </Button>
            </div>

            <div className="h-2" />
          </div>
        </div>
      </Sheet>
    </div>
  );
}
